import { useId, useRef, useState } from "react";
import { FileDropTarget } from "@/components/FileDropTarget";
import { FieldMarker } from "@/components/FieldMarker";
import { SelectField } from "@/components/SelectField";
import { messageFor } from "@/lib/errors";
import {
  asDocuments,
  type BondFieldValue,
  type BondRenderField,
} from "@/lib/bondFields";
import { documentRejection, type BondDocumentRef } from "@/lib/brokerBonds";
import {
  acceptAttribute,
  documentRuleMessage,
  effectiveMaxFiles,
  formatFileSize,
  screenDocument,
  uploadableTypes,
  DEFAULT_UPLOAD_TYPE,
  type DocumentRules,
  type DocumentTypeInfo,
  type DocumentTypeKey,
} from "@/lib/brokerDocumentTypes";

// BRK-S145 — the control for a `files` configuration field.
//
// The upload is PART OF INTAKE: the configuration asks for documents the way it
// asks for a date, so this is a control on the step and not a side panel. Each
// added file is POSTed on its own and the reference PMS returns is written into
// the step's value — which is what makes the field's answer a fact about the
// SERVER rather than about this component's state.
//
// ── NO OPTIMISTIC SUCCESS ────────────────────────────────────────────────────
//
// A file appears in the attached list only once PMS has answered with its
// reference, because the attached list IS the value: `asDocuments(value)` is
// what renders, never a local "uploaded" flag. So a rejected or failed file
// cannot appear attached and cannot enter the value — not by discipline, but
// because there is no code path that would put it there. That is the BRK-S122
// defect class, closed by construction rather than by remembering to roll back.
//
// In-flight files live in their OWN list beside the attached one, visibly
// distinct: "Uploading 60%" is not "attached", and a broker deciding whether
// they can hit Continue must never be able to read one as the other.

/** What the field needs from the page: the served rules, and the two writes. */
export interface BondFilesPort {
  /** PMS's served upload rules, or null while they load / if the read failed. */
  rules: DocumentRules | null;
  /**
   * The types a broker may upload, as PMS serves them — what the picker offers.
   *
   * SERVED, never a local list: PMS's own schema calls this endpoint the thing
   * "BRK's upload picker reads instead of hardcoding a list", and the labels are
   * declared in its registry beside the rules precisely so an admin screen and
   * a broker's picker cannot call one type two things.
   */
  types: readonly DocumentTypeInfo[];
  /** Why the rules are unavailable, when they are. */
  rulesError: string | null;
  /**
   * How one attached document's kind reads on its row.
   *
   * The PAGE decides, because it is the half that can know: PMS's document
   * TYPE lives on the documents list (`GET …/documents`), not on the four-field
   * reference stored in the value bag, so a resumed order has only the
   * `contentType` until that list is read. Passing both means the row shows the
   * real type label when it is known and an honest file kind ("PDF") when it is
   * not — never a type invented from a filename.
   */
  typeLabel: (document: BondDocumentRef) => string;
  /** A filed order is a record, not a form. */
  readOnly: boolean;
  /** Upload one file to one field, reporting progress as a whole percent. */
  upload: (
    fieldKey: string,
    file: File,
    type: DocumentTypeKey,
    onProgress: (percent: number) => void,
  ) => Promise<BondDocumentRef>;
  /** Detach one stored document. */
  remove: (documentId: string) => Promise<void>;
}

/** One file the broker added that has not settled yet. */
interface Pending {
  id: string;
  filename: string;
  percent: number;
}

/** A file that will not be attached — screened out here, or refused by PMS. */
interface Problem {
  id: string;
  message: string;
}

let seq = 0;
const nextId = () => `bf${String((seq += 1))}`;

export function BondFilesField({
  field,
  value,
  onChange,
  port,
  error,
}: {
  field: BondRenderField;
  value: BondFieldValue | undefined;
  onChange: (value: BondDocumentRef[] | undefined) => void;
  port: BondFilesPort;
  error?: string | null;
}) {
  const attached = asDocuments(value);
  const [pending, setPending] = useState<Pending[]>([]);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  // What the NEXT file will be filed as. Defaulted to PMS's own default rather
  // than to the first served row, so an untouched picker and an omitted `type`
  // mean the same thing.
  const [uploadType, setUploadType] =
    useState<DocumentTypeKey>(DEFAULT_UPLOAD_TYPE);
  const labelId = useId();
  // The LATEST value, for the upload loop below. It awaits between files, and a
  // props closure is a render old by the second one — which really did drop the
  // first file's reference until a test for two files caught it.
  const valueRef = useRef(value);
  valueRef.current = value;

  const { rules, rulesError, readOnly } = port;
  // Belt and braces: the endpoint already serves only the uploadable subset.
  const offered = uploadableTypes(port.types);
  const cap = rules ? effectiveMaxFiles(rules, field.maxFiles) : undefined;
  const full = cap !== undefined && attached.length + pending.length >= cap;

  async function add(files: File[]) {
    if (!rules || readOnly) return;
    // A fresh batch replaces the last batch's complaints: they were about files
    // the broker has moved on from, and leaving them makes a field that has
    // just succeeded look like it failed.
    setProblems([]);

    const accepted: { file: File; pendingId: string }[] = [];
    const refused: Problem[] = [];
    // Counted UP as the batch is screened, so dropping eleven files onto a
    // ten-file field refuses the eleventh — not all eleven, and not none.
    let held = attached.length + pending.length;
    for (const file of files) {
      const rejection = screenDocument(file, rules, {
        attachedCount: held,
        maxFiles: field.maxFiles,
      });
      if (rejection) {
        refused.push({ id: nextId(), message: rejection.message });
        continue;
      }
      accepted.push({ file, pendingId: nextId() });
      held += 1;
    }
    if (refused.length) setProblems(refused);
    if (accepted.length === 0) return;

    setPending((p) => [
      ...p,
      ...accepted.map(({ file, pendingId }) => ({
        id: pendingId,
        filename: file.name,
        percent: 0,
      })),
    ]);

    // ACCUMULATED locally across the batch, seeded from the latest value. Each
    // `onChange` sends the whole list, so nothing depends on React having
    // committed the previous write before the next `await` resumes — which it
    // need not, and which is how a two-file drop lost its first file.
    let list = asDocuments(valueRef.current);

    // Sequential, not parallel. A broker attaching four scans on hotel wi-fi
    // gets four bars that finish one after another rather than four that crawl
    // together, and PMS sees one write at a time against one order — which
    // matters because the per-field COUNT is enforced server-side, and four
    // simultaneous posts against a cap of ten with nine held is a race.
    for (const { file, pendingId } of accepted) {
      try {
        const document = await port.upload(
          field.name,
          file,
          uploadType,
          (percent) => {
            setPending((p) =>
              p.map((x) => (x.id === pendingId ? { ...x, percent } : x)),
            );
          },
        );
        setPending((p) => p.filter((x) => x.id !== pendingId));
        list = [...list, document];
        onChange(list);
      } catch (err) {
        setPending((p) => p.filter((x) => x.id !== pendingId));
        setProblems((p) => [
          ...p,
          { id: nextId(), message: uploadProblem(err, file, rules) },
        ]);
      }
    }
  }

  async function detach(documentId: string) {
    if (readOnly) return;
    setRemoving(documentId);
    setProblems([]);
    try {
      await port.remove(documentId);
      const next = asDocuments(valueRef.current).filter(
        (d) => d.documentId !== documentId,
      );
      // Written as ABSENT rather than `[]` when it empties: everywhere else in
      // this flow an unanswered field is omitted, never sent as "", and an
      // empty array makes the same false claim — "answered, with nothing".
      onChange(next.length === 0 ? undefined : next);
    } catch (err) {
      setProblems([{ id: nextId(), message: messageFor(err) }]);
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className="flex w-full flex-col gap-2 text-left">
      <span className="flex items-baseline gap-1.5">
        <span id={labelId} className="text-sm font-medium text-brk-text">
          {field.label}
        </span>
        <FieldMarker required={field.required} optional={!field.required} />
      </span>
      {field.help && <p className="text-xs text-brk-dim">{field.help}</p>}

      {/* The attached list comes FIRST and renders from the VALUE, so what the
          field shows and what the order records are one list. */}
      {attached.length > 0 && (
        <ul aria-labelledby={labelId} className="flex flex-col gap-2">
          {attached.map((doc) => (
            <li
              key={doc.documentId}
              className="flex flex-col gap-1 rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium text-brk-text">
                  {doc.filename}
                </span>
                <span className="text-xs text-brk-dim">
                  {port.typeLabel(doc)} · {formatFileSize(doc.byteSize)}
                </span>
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => void detach(doc.documentId)}
                  disabled={removing !== null}
                  className="shrink-0 self-start text-sm text-brk-danger hover:underline disabled:opacity-60 sm:self-auto"
                >
                  {removing === doc.documentId ? "Removing…" : "Remove"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pending.length > 0 && (
        <ul className="flex flex-col gap-2">
          {pending.map((p) => (
            <li
              key={p.id}
              className="flex flex-col gap-1 rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-brk-text">
                  {p.filename}
                </span>
                {/* The word matters: "Uploading 60%" is not "Attached". */}
                <span className="shrink-0 text-xs text-brk-dim">
                  Uploading {p.percent}%
                </span>
              </div>
              <div
                className="h-1 w-full overflow-hidden rounded-full bg-brk-border"
                role="progressbar"
                aria-label={`Uploading ${p.filename}`}
                aria-valuenow={p.percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full bg-brk-accent transition-all"
                  style={{ width: `${String(p.percent)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      {readOnly ? (
        attached.length === 0 && (
          <p className="text-sm text-brk-dim">No documents were attached.</p>
        )
      ) : rulesError !== null ? (
        // NOT a silent drop zone. The served rules are the only thing that says
        // which files PMS takes, so without them every upload is a guess the
        // broker pays for — the same argument `UnsupportedField` makes for a
        // control this bundle cannot draw (D-10 rule 1).
        <div className="flex flex-col gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-4">
          <p role="alert" className="text-sm text-amber-900">
            We can’t take attachments right now — reload the page and try again.
            If it keeps happening, contact us and we’ll take them by email.
          </p>
          <p className="text-xs text-amber-800">{rulesError}</p>
        </div>
      ) : rules === null ? (
        <p role="status" className="text-sm text-brk-dim">
          Loading what we can accept…
        </p>
      ) : full ? (
        <p className="text-sm text-brk-dim">
          {documentRuleMessage("too_many_files", "", rules, { cap })}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {offered.length > 1 && (
            <SelectField
              label="What is this?"
              options={offered.map((t) => ({ value: t.key, label: t.label }))}
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value as DocumentTypeKey)}
            />
          )}
          <FileDropTarget
            prompt="Drag & drop your documents here, or"
            hint={fileHint(rules, cap)}
            inputLabel={`Attach files for ${field.label}`}
            accept={
              field.accept && field.accept.length > 0
                ? field.accept.join(",")
                : acceptAttribute(rules)
            }
            onFiles={(files) => void add(files)}
          />
        </div>
      )}

      {problems.length > 0 && (
        <ul
          role="alert"
          className="flex flex-col gap-1 text-sm text-brk-danger"
        >
          {problems.map((p) => (
            <li key={p.id}>{p.message}</li>
          ))}
        </ul>
      )}

      {/* The STEP's complaint about this field (a missing required answer, or a
          server error anchored here) — the same slot every other control uses. */}
      {error && <p className="text-sm text-brk-danger">{error}</p>}
    </div>
  );
}

/** The limits, said out loud under the drop zone — read off the SERVED rules,
 *  so the sentence cannot drift from the check or from what PMS accepts. */
function fileHint(rules: DocumentRules, cap: number | undefined): string {
  const kinds = rules.allowedExtensions.join(", ");
  const size = `up to ${formatFileSize(rules.maxBytesPerFile)} each`;
  const count = cap === undefined ? null : `${String(cap)} files max`;
  return [kinds, size, count].filter((part) => part !== null).join(" · ");
}

/**
 * A failed upload as one sentence.
 *
 * PMS's own structured refusal wins when it made one — it read the bytes and we
 * did not, so its verdict on a file that got past `screenDocument` is the
 * accurate one — and it is said in the SAME words the client-side check uses
 * (`documentRuleMessage`), because one broken rule described two ways teaches a
 * broker that they are two problems. Anything else is a request failure and
 * reads as one, with the retry implied: the file is fine, the call was not.
 */
function uploadProblem(err: unknown, file: File, rules: DocumentRules): string {
  const rejection = documentRejection(err);
  if (rejection) {
    return documentRuleMessage(rejection.rule, rejection.filename, rules, {
      size: file.size,
    });
  }
  return `“${file.name}” didn’t upload — ${messageFor(err)}`;
}
